import React, { useState } from 'react';

const ChannelModal = ({ channel, onClose, onSave }) => {
  const isNew = !channel;
  
  const [formData, setFormData] = useState({
    channel_name: channel?.channel_name || '',
    channel_id: channel?.channel_id || '',
    loan_number: channel?.loan_number || '',
    status: channel?.status || 'Active',
    loan_status: channel?.loan_status || ''
  });
  
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error when field is updated
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };
  
  const validate = () => {
    const newErrors = {};
    
    if (!formData.channel_name.trim()) {
      newErrors.channel_name = 'Channel Name is required';
    }
    
    if (!formData.loan_number.trim()) {
      newErrors.loan_number = 'Loan Number is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }
    
    const channelData = {
      ...formData
    };
    
    if (!isNew) {
      channelData.id = channel.id;
    }
    
    try {
      setIsLoading(true);
      await onSave(channelData, isNew);
    } catch (error) {
      // Error handling is done in the parent component
      console.error('Error saving channel:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
        <h2 className="text-xl font-bold text-gray-900 mb-4">
          {isNew ? 'Add New Channel' : 'Edit Channel'}
        </h2>
        
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label htmlFor="channel_name" className="block text-sm font-medium text-gray-700 mb-1">
                Channel Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="channel_name"
                name="channel_name"
                value={formData.channel_name}
                onChange={handleChange}
                disabled={isLoading}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 ${
                  errors.channel_name ? 'border-red-500' : 'border-gray-300'
                } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
              />
              {errors.channel_name && (
                <p className="mt-1 text-sm text-red-500">{errors.channel_name}</p>
              )}
            </div>
            
            <div>
              <label htmlFor="channel_id" className="block text-sm font-medium text-gray-700 mb-1">
                Channel ID
              </label>
              <input
                type="text"
                id="channel_id"
                name="channel_id"
                value={formData.channel_id}
                onChange={handleChange}
                disabled={isLoading}
                className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 ${
                  isLoading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              />
            </div>
            
            <div>
              <label htmlFor="loan_number" className="block text-sm font-medium text-gray-700 mb-1">
                Loan Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="loan_number"
                name="loan_number"
                value={formData.loan_number}
                onChange={handleChange}
                disabled={isLoading}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 ${
                  errors.loan_number ? 'border-red-500' : 'border-gray-300'
                } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
              />
              {errors.loan_number && (
                <p className="mt-1 text-sm text-red-500">{errors.loan_number}</p>
              )}
            </div>
            
            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                disabled={isLoading}
                className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 ${
                  isLoading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                <option value="Active">Active</option>
                <option value="Archived">Archived</option>
              </select>
            </div>
            
            <div>
              <label htmlFor="loan_status" className="block text-sm font-medium text-gray-700 mb-1">
                Loan Status
              </label>
              <input
                type="text"
                id="loan_status"
                name="loan_status"
                value={formData.loan_status}
                onChange={handleChange}
                disabled={isLoading}
                className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 ${
                  isLoading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              />
            </div>
          </div>
          
          <div className="mt-8 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className={`px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 ${
                isLoading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className={`px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 flex items-center justify-center ${
                isLoading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isLoading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
              ) : (
                isNew ? 'Create Channel' : 'Update Channel'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChannelModal;